import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppMode,
  EmbeddingState,
  IntegrationState,
  OutboxEntry,
  OutboxStatus,
  ProjectionKind,
  ReconciliationReport,
  ReconciliationRow,
  ResolveOutboxBody,
} from '@blesk/contracts';
import { apiGet, apiPost } from '../../shared/api';
import { usePagedList } from '../../shared/lists';
import {
  CATALOG_STATUS_LABEL,
  OUTBOX_COMMAND_LABEL,
  OUTBOX_STATUS_LABEL,
  PROJECTION_KIND_LABEL,
} from '../../shared/labels';
import { formatInProjectTz } from '../../shared/format';
import { useHasPermission, useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import { Dialog, EmptyRow, ErrorNote, Pager, orDash } from '../shell/ui';

/** §13, §17.2 ТЗ, E7/E10. Раздел «Интеграции и журнал ошибок». */
export function IntegrationPage() {
  return (
    <PermissionGate permission="integration.read">
      <IntegrationInner />
    </PermissionGate>
  );
}

const BREAKER_LABEL: Record<string, string> = {
  CLOSED: 'замкнут — операции идут',
  OPEN: 'разомкнут — команды копятся в очереди',
  HALF_OPEN: 'пробный запрос',
};

/**
 * Ручной повтор допустим только из этих состояний (§17.2, сценарий 34).
 *
 * `NEEDS_RECONCILIATION` сюда не входит: результат прошлой попытки неизвестен,
 * и §17.2 запрещает повторять такую операцию вслепую. Для неё есть разбор.
 */
const RETRYABLE = ['FAILED', 'NEEDS_ATTENTION'];

/**
 * Подпись плитки очереди для состояния «Требует сверки».
 *
 * Плитка считает команды, а отчёт ниже — записи без проекции, у которых команды
 * может не быть вовсе. Под одним названием это читалось как одно число, дважды
 * показанное с разными значениями.
 */
const QUEUE_TILE_LABEL: Partial<Record<OutboxStatus, string>> = {
  NEEDS_RECONCILIATION: 'Команды в сверке',
};

function IntegrationInner() {
  const session = useSession();
  const canRetry = useHasPermission('integration.retry');
  const queryClient = useQueryClient();
  const [resolving, setResolving] = useState<OutboxRef | null>(null);

  const [status, setStatus] = useUrlFilter('status');
  const [command, setCommand] = useUrlFilter('command');

  const state = useQuery({
    queryKey: ['integration', 'state'],
    queryFn: () => apiGet<IntegrationState>('/api/integration/state'),
    // очередь живёт своей жизнью: состояние обновляется само, без перезагрузки
    refetchInterval: 15_000,
  });

  const journal = usePagedList<OutboxEntry>('integration', '/api/integration/journal', {
    status,
    command,
  });

  const retry = useMutation({
    mutationFn: (id: string) => apiPost(`/api/integration/outbox/${id}/retry`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integration'] }),
  });

  return (
    <>
      <h1>Интеграции и журнал ошибок</h1>
      <p className="muted">
        Бизнес-события принимаются всегда; внешняя операция живёт в очереди и повторяется.
      </p>

      <ErrorNote error={state.error ?? journal.error ?? retry.error} />

      {state.data && (
        <StateSummary state={state.data} mode={session.mode} timezone={session.defaultTimezone} />
      )}

      <Embedding timezone={session.defaultTimezone} />

      <Reconciliation
        timezone={session.defaultTimezone}
        canResolve={canRetry}
        onResolve={setResolving}
      />

      <h2>Очередь операций</h2>

      {/* Плитки стоят у своей таблицы: они и есть отбор по статусу, а не сводка
          в шапке раздела, от которой непонятно, на что она влияет. */}
      {state.data && (
        <div className="stat-grid">
          {Object.entries(state.data.counts).map(([key, value]) => {
            const active = status === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                className={`stat stat--toggle${active ? ' stat--active' : ''}`}
                onClick={() => setStatus(active ? '' : key)}
              >
                <span className="stat__value">{value}</span>
                <span className="stat__label">
                  {QUEUE_TILE_LABEL[key as OutboxStatus] ??
                    OUTBOX_STATUS_LABEL[key as OutboxStatus] ??
                    key}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {state.data && state.data.attentionCount > 0 && (
        <p className="error" role="status">
          Требует внимания: {state.data.attentionCount}. Это подтверждённые ошибки и операции с
          неизвестным результатом — их разбирают вручную.
        </p>
      )}

      <div className="toolbar">
        <label>
          Статус
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(OUTBOX_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Команда
          <select value={command} onChange={(event) => setCommand(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(OUTBOX_COMMAND_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Команда</th>
              <th>Назначение</th>
              <th>Статус</th>
              {/* Счётчик пишется только в markFailed: успех его не трогает, и «0» у
                  выполненной команды значит «прошло с первого раза». Подпись «Попыток»
                  читалась как враньё счётчика (QA 22.08.2026, дефект 7). */}
              <th>Неудач</th>
              <th>Объект записи</th>
              <th>Последняя ошибка</th>
              <th>Следующая попытка</th>
              <th>Обновлено</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {journal.items.length === 0 && <EmptyRow colSpan={9} loading={journal.isPending} />}
            {journal.items.map((entry) => (
              <tr key={entry.id}>
                <td>{OUTBOX_COMMAND_LABEL[entry.command] ?? entry.command}</td>
                <td>{entry.target}</td>
                <td>{OUTBOX_STATUS_LABEL[entry.status]}</td>
                <td>{entry.attempts}</td>
                <td>
                  {orDash(entry.entityType)}
                  {entry.entityId && <span className="muted"> · {entry.entityId.slice(0, 8)}</span>}
                </td>
                <td className="cell-wrap">{orDash(entry.lastError)}</td>
                <td>{formatInProjectTz(entry.nextAttemptAt, session.defaultTimezone)}</td>
                <td>{formatInProjectTz(entry.updatedAt, session.defaultTimezone)}</td>
                <td className="row-actions">
                  {canRetry && entry.status === 'NEEDS_RECONCILIATION' && (
                    <button
                      type="button"
                      onClick={() => setResolving({ id: entry.id, command: entry.command })}
                    >
                      Разобрать
                    </button>
                  )}
                  {canRetry && RETRYABLE.includes(entry.status) && (
                    <button
                      type="button"
                      disabled={retry.isPending}
                      onClick={() => retry.mutate(entry.id)}
                    >
                      Повторить
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager
        page={journal.page}
        perPage={journal.perPage}
        total={journal.total}
        onPage={journal.setPage}
      />

      <p className="muted">
        Содержимое команд наружу не отдаётся: в тексте сообщения могут быть персональные данные. В
        журнале видны адрес операции, статус и безопасное описание ошибки.
      </p>

      {resolving && <ResolveDialog entry={resolving} onClose={() => setResolving(null)} />}
    </>
  );
}

function StateSummary({
  state,
  mode,
  timezone,
}: {
  state: IntegrationState;
  mode: AppMode;
  timezone: string;
}) {
  return (
    <dl className="detail">
      <dt>Интеграция</dt>
      <dd>
        {/* Бейдж в шапке выводится из APP_MODE, эта строка — из адаптера.
            Включённый адаптер при автономном режиме бывает только тестовым —
            говорим это явно, чтобы строка не противоречила шапке. */}
        {!state.integrationEnabled
          ? 'выключена — автономный режим, задачи в Bitrix24 не создаются'
          : mode === 'STANDALONE'
            ? 'включена с тестовым адаптером — команды исполняются, но не в живом Bitrix24'
            : 'включена'}
      </dd>
      <dt>Доступность Bitrix24</dt>
      <dd>
        <span className={`badge ${state.b24Available ? 'badge--ok' : 'badge--err'}`}>
          {state.b24Available ? 'доступен' : 'недоступен'}
        </span>
      </dd>
      <dt>Предохранитель</dt>
      <dd>
        {BREAKER_LABEL[state.breaker.state] ?? state.breaker.state}
        {state.breaker.reason && ` · ${state.breaker.reason}`}
      </dd>
      {/* У замкнутой цепи оба времени пусты: два прочерка в списке состояния
          выглядят как потерянные данные, а не как «размыкания не было». */}
      {state.breaker.state !== 'CLOSED' && (
        <>
          <dt>Разомкнут с</dt>
          <dd>{formatInProjectTz(state.breaker.openedAt, timezone)}</dd>
          <dt>Следующая проба</dt>
          <dd>{formatInProjectTz(state.breaker.nextProbeAt, timezone)}</dd>
        </>
      )}
      <dt>Самая старая команда</dt>
      <dd>{oldestPendingLabel(state.oldestPendingAgeSeconds)}</dd>
    </dl>
  );
}

/**
 * Пустая очередь и команда, поставленная только что, — разные новости: первая
 * говорит «работы нет», вторая — «работа идёт». Сервер различает их через
 * `null`, и сводить обе к прочерку интерфейс не имеет права.
 */
function oldestPendingLabel(seconds: number | null): string {
  if (seconds === null) return 'очередь пуста';
  if (seconds < 60) return 'ждёт меньше минуты';
  return `ждёт ${Math.round(seconds / 60)} мин`;
}

/**
 * §15.1, T40. Встройка в Bitrix24: как портал видит наше приложение.
 *
 * Раздел появился по разбору 21.08.2026. Пункт левого меню не открывался у
 * рядового сотрудника портала, и двое суток это выглядело нашей поломкой:
 * встройка работала, журнал контура был чист. Приложение стояло в статусе
 * `PRIVATE`, а такое портал показывает только его автору — и увидеть это в
 * системе было негде. Теперь есть где.
 */
function Embedding({ timezone }: { timezone: string }) {
  const embedding = useQuery({
    queryKey: ['integration', 'embedding'],
    queryFn: () => apiGet<EmbeddingState>('/api/integration/embedding'),
  });

  if (!embedding.data) return <ErrorNote error={embedding.error} />;
  const data = embedding.data;

  if (!data.configured) {
    return (
      <>
        <h2>Встройка в Bitrix24</h2>
        <p className="muted">
          Вход через портал не настроен: приложение открывается по своему адресу, а не изнутри
          Bitrix24. Это режим, а не поломка.
        </p>
      </>
    );
  }

  if (data.unavailable) {
    return (
      <>
        <h2>Встройка в Bitrix24</h2>
        <p className="error" role="status">
          Шлюз VibeCode не ответил — состояние встройки неизвестно. Это не значит, что встройка
          сломана: на вход сотрудников раздел не влияет.
        </p>
      </>
    );
  }

  const isPrivate = data.catalogStatus === 'PRIVATE';

  return (
    <>
      <h2>Встройка в Bitrix24</h2>

      <dl className="detail">
        <dt>Статус в каталоге портала</dt>
        <dd>
          <span
            className={`badge ${data.catalogStatus === 'PUBLISHED' ? 'badge--ok' : 'badge--err'}`}
          >
            {data.catalogStatus
              ? (CATALOG_STATUS_LABEL[data.catalogStatus] ?? data.catalogStatus)
              : 'шлюз не назвал'}
          </span>
        </dd>
        {data.publishedAt && (
          <>
            <dt>Опубликовано</dt>
            <dd>{formatInProjectTz(data.publishedAt, timezone)}</dd>
          </>
        )}
        <dt>Адрес приложения</dt>
        <dd>
          {orDash(data.appUrl)}
          {/* Шлюз сверяет адрес с зарегистрированным посимвольно (S3): расхождение
              даёт отказ на входе, и увидеть его надо здесь, а не в журнале. */}
          {!data.appUrlMatches && (
            <div className="error">Не совпадает с адресом сервиса: {data.appBaseUrl}</div>
          )}
        </dd>
        <dt>Идентификатор в Bitrix24</dt>
        <dd>{orDash(data.bitrixClientId)}</dd>
        <dt>Сверка с порталом</dt>
        <dd>
          {data.portalSync === 'ok'
            ? 'портал подтвердил привязку'
            : // Без пользовательской сессии шлюз не спрашивает портал вовсе.
              // Это «не проверяли», а не «привязки нет», и путать их нельзя.
              'не проверялась — серверу шлюз отвечает только о себе'}
          {data.portalSyncReason && <span className="muted"> · {data.portalSyncReason}</span>}
        </dd>
      </dl>

      {isPrivate && (
        <p className="error" role="status">
          Приложение видит только его автор. Сотрудники портала на пункте меню получают «Нет
          доступа», пока приложение не опубликовано в каталоге аккаунта.
        </p>
      )}

      {/* Замка два, и второй шлюзу не виден: список сотрудников у приложения
          живёт только в интерфейсе портала, метода REST для него нет, и
          публикация его не трогает. Поэтому `PUBLISHED` ещё не означает
          «сотрудник войдёт» — разбор 21.08.2026, `spikes/S3-vibecode.md`. */}
      <p className="muted">
        Статуса каталога мало: у приложения есть отдельный список сотрудников, и после создания в
        нём только автор. Он правится в портале — Разработчикам → Интеграции → строка приложения →
        «Права доступа для сотрудников», — и отсюда не виден: метода для его чтения у шлюза нет.
      </p>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Место встраивания</th>
              <th>Подпись</th>
              <th>Обработчик</th>
              <th>Привязка</th>
            </tr>
          </thead>
          <tbody>
            {data.placements.length === 0 && <EmptyRow colSpan={4} loading={embedding.isPending} />}
            {data.placements.map((placement) => (
              <tr key={placement.code}>
                <td>{placement.code}</td>
                <td>{orDash(placement.title)}</td>
                <td className="cell-wrap">{orDash(placement.handler)}</td>
                <td>
                  {placement.misbound === null
                    ? '—'
                    : placement.misbound
                      ? 'испорчена — портал не примет'
                      : 'цела'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="muted">
        Снимок от {formatInProjectTz(data.readAt, timezone)}; обновляется не чаще раза в минуту.
        Публикация и отзыв приложения делаются в кабинете портала, отсюда — только чтение.
      </p>
    </>
  );
}

/** Строка очереди, по которой ведётся разбор: id и команда — всё, что нужно диалогу. */
interface OutboxRef {
  id: string;
  command: string;
}

const PROJECTION_KINDS: readonly ProjectionKind[] = ['CRM_CONTACT', 'LIST_ROW', 'TASK'];

/**
 * Сколько строк отчёта показывать сразу и потолок кнопки «Показать все»
 * (500 — максимум `limit` в контракте). Полный отчёт в сотни строк отодвигал
 * очередь операций далеко вниз, а нужны они на одном экране.
 */
const REPORT_PREVIEW = 20;
const REPORT_MAX = 500;

/**
 * §17.3. Отчёт о записях без ожидаемой проекции в Bitrix24.
 *
 * Отдельно от журнала, потому что отвечает на другой вопрос. Журнал показывает,
 * что случилось с командой; отчёт — что расходится с Bitrix24 прямо сейчас,
 * включая случай, когда команды нет вовсе и в журнале искать нечего.
 */
function Reconciliation({
  timezone,
  canResolve,
  onResolve,
}: {
  timezone: string;
  canResolve: boolean;
  onResolve: (entry: OutboxRef) => void;
}) {
  const queryClient = useQueryClient();
  const [expected, setExpected] = useUrlFilter('expected');
  const [limit, setLimit] = useState(REPORT_PREVIEW);
  // В адресе может оказаться что угодно: неизвестный вид отбором не считаем
  // и в запрос не отправляем.
  const kind = PROJECTION_KINDS.find((value) => value === expected);

  const report = useQuery({
    queryKey: ['integration', 'reconciliation', kind ?? '', limit],
    queryFn: () =>
      apiGet<ReconciliationReport>('/api/integration/reconciliation', { expected: kind, limit }),
  });

  /**
   * §17: у строки «команда не ставилась вовсе» раньше не было ни одного
   * действия — отчёт ставил задачу и не давал её решить (QA 22.08.2026,
   * дефект 5). Постановка идемпотентна по dedup-ключу; дальше команда живёт
   * по общим правилам очереди и видна в журнале ниже.
   */
  const enqueue = useMutation({
    mutationFn: (row: ReconciliationRow) =>
      apiPost('/api/integration/reconciliation/enqueue', {
        entityType: row.entityType,
        entityId: row.entityId,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integration'] }),
  });

  if (!report.data) return <ErrorNote error={report.error} />;
  const data = report.data;
  // Итог считает сервер: при отборе по виду это счётчик выбранного вида,
  // без отбора — общий. В браузере ничего не складывается.
  const selectedTotal = kind ? data.counts[kind] : data.counts.total;

  return (
    <>
      <h2>Записи без проекции в Bitrix24 — {data.counts.total}</h2>
      <p className="muted">
        Записи, у которых нет ожидаемой проекции в Bitrix24. Свежее {data.graceMinutes} мин не
        показывается: такая операция ещё в очереди.
        {data.listsDisabled && ' Проекция списков выключена настройкой — строк списка не ждём.'}
      </p>

      {!data.integrationEnabled ? (
        <p className="muted">Автономный режим: проекций в Bitrix24 не ожидается, сверять нечего.</p>
      ) : (
        <>
          <div className="stat-grid">
            {PROJECTION_KINDS.filter((value) => !(value === 'LIST_ROW' && data.listsDisabled)).map(
              (value) => {
                const active = kind === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={active}
                    className={`stat stat--toggle${active ? ' stat--active' : ''}`}
                    onClick={() => setExpected(active ? '' : value)}
                  >
                    <span className="stat__value">{data.counts[value]}</span>
                    <span className="stat__label">{PROJECTION_KIND_LABEL[value]}</span>
                  </button>
                );
              },
            )}
          </div>

          <ErrorNote error={enqueue.error} />

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Запись</th>
                  <th>Объект</th>
                  <th>Ожидается</th>
                  <th>С какого времени</th>
                  <th>Причина</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && <EmptyRow colSpan={6} loading={report.isPending} />}
                {data.items.map((row) => (
                  <tr key={`${row.entityType}:${row.entityId}`}>
                    <td>
                      <RecordLink row={row} />
                    </td>
                    <td>{orDash(row.projectName)}</td>
                    <td>{PROJECTION_KIND_LABEL[row.expected]}</td>
                    <td>{formatInProjectTz(row.expectedSince, timezone)}</td>
                    {/* Одна колонка на две прежние: состояние команды и объяснение
                        расходились только там, где команды нет вовсе. */}
                    <td className="cell-wrap">
                      {row.outbox && <div>{OUTBOX_STATUS_LABEL[row.outbox.status]}</div>}
                      {row.reason && <div className="muted">{row.reason}</div>}
                    </td>
                    <td className="row-actions">
                      {canResolve && !row.outbox && (
                        <button
                          type="button"
                          disabled={enqueue.isPending}
                          onClick={() => enqueue.mutate(row)}
                        >
                          Поставить в очередь
                        </button>
                      )}
                      {canResolve && row.outbox?.status === 'NEEDS_RECONCILIATION' && (
                        <button
                          type="button"
                          onClick={() =>
                            onResolve({ id: row.outbox!.outboxId, command: row.outbox!.command })
                          }
                        >
                          Разобрать
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.truncated && (
            <p className="muted">
              Показаны {data.items.length} из {selectedTotal} — самые давние.{' '}
              {limit < REPORT_MAX ? (
                <button type="button" onClick={() => setLimit(REPORT_MAX)}>
                  Показать все
                </button>
              ) : (
                'Больше за один раз отчёт не отдаёт: отберите по виду проекции.'
              )}
            </p>
          )}
        </>
      )}
    </>
  );
}

/**
 * Ссылка со строки отчёта на саму запись (§13).
 *
 * Без неё строка — тупик: расхождение видно, а перейти к инциденту, обращению
 * или человеку неоткуда, и номер приходится искать в другом разделе руками.
 */
function RecordLink({ row }: { row: ReconciliationRow }) {
  switch (row.entityType) {
    case 'incident':
      return (
        <Link to="/incidents" search={{ number: row.title }}>
          Инцидент {row.title}
        </Link>
      );
    case 'customer_request':
      return (
        <Link to="/requests" search={{ number: row.title }}>
          Обращение {row.title}
        </Link>
      );
    case 'person':
      return (
        <Link to="/people" search={{ search: row.title }}>
          {row.title}
        </Link>
      );
    case 'project_admin':
      // Человека ищем по имени, а объект сужает список до нужной связи:
      // один и тот же человек бывает администратором нескольких объектов.
      return (
        <Link to="/people" search={{ search: row.title, projectId: row.projectId ?? undefined }}>
          {row.title}
        </Link>
      );
  }
}

/**
 * §17.2, §17.3, сценарий 35. Разбор «Требует сверки».
 *
 * Система не смогла узнать, применилась ли операция, и повторять её вслепую
 * не имеет права. Здесь человек, посмотревший портал, отвечает за неё.
 */
function ResolveDialog({ entry, onClose }: { entry: OutboxRef; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<ResolveOutboxBody['decision']>('APPLIED');
  const [externalId, setExternalId] = useState('');
  const [note, setNote] = useState('');

  const needsExternalId = decision === 'APPLIED' && entry.command !== 'SEND_MESSAGE';

  const resolve = useMutation({
    mutationFn: () =>
      apiPost(`/api/integration/outbox/${entry.id}/resolve`, {
        decision,
        externalId: needsExternalId ? externalId.trim() : undefined,
        note: note.trim() || undefined,
      } satisfies ResolveOutboxBody),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integration'] });
      onClose();
    },
  });

  return (
    <Dialog
      title={`Сверка: ${OUTBOX_COMMAND_LABEL[entry.command] ?? entry.command}`}
      onClose={onClose}
      // Идентификатор и подтверждение человек добывает в портале: закрыть
      // диалог случайным Esc и идти туда второй раз — потерянная работа.
      confirmClose={
        externalId || note ? 'Закрыть без записи решения? Введённое пропадёт.' : undefined
      }
    >
      <p className="muted">
        Результат прошлой попытки неизвестен, и повторить её вслепую нельзя. Посмотрите Bitrix24 и
        скажите, что там есть.
      </p>

      <ErrorNote error={resolve.error} />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          resolve.mutate();
        }}
      >
        <label>
          <input
            type="radio"
            name="decision"
            checked={decision === 'APPLIED'}
            onChange={() => setDecision('APPLIED')}
          />
          Операция выполнена — запись в Bitrix24 есть
        </label>
        <label>
          <input
            type="radio"
            name="decision"
            checked={decision === 'NOT_APPLIED'}
            onChange={() => setDecision('NOT_APPLIED')}
          />
          Операции нет — выполнить заново
        </label>

        {needsExternalId && (
          <label>
            Идентификатор в Bitrix24
            <input
              value={externalId}
              onChange={(event) => setExternalId(event.target.value)}
              required
              placeholder="номер задачи или контакта"
            />
          </label>
        )}

        <label>
          Чем подтверждено (в аудит, без персональных данных)
          <input value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} />
        </label>

        <div className="dialog__actions">
          <button type="submit" disabled={resolve.isPending}>
            Записать решение
          </button>
          <button type="button" onClick={onClose}>
            Отмена
          </button>
        </div>
      </form>
    </Dialog>
  );
}
